import * as React from "react"
import { connect } from "react-redux"
import trans, { isTrans } from "trans";

import {
    FiltersFormType,
    ElementsFilterType,
    RangeType,
} from "../types/filtertypes";

import {
    css,
    Grid,
    GridCell,
    TextInput,
    Toggler,
    Theme,
    Spinner,
    Icon,
    Select,
} from "@bdfx/mergado-ui-kit";

import { iconDisabledColor } from "template-edit/components/tools-panel";
import { FIEQueryType } from "./fie-queries";
import { deleteFIEQuery, projectId, saveFilterAsQuery } from "app/helpers/api";
import { setFIEQueriesToStore } from "../reducer";
import { ViewTableType } from "../containers";
import { elementPrefix } from "./save-as-query-modal-wrapper";
import { ID, intboolean } from "app/types/app-types";
import Mergado from "app/helpers/mergado";
import store from "store";
import { createToast } from "actions";
import { textToast } from "app/helpers/toast_factory";
import { FType, getNonEmptyFiltersForType } from "./filters";

import {
    updateInputValue,
} from "app/reducers/form";
import { AxiosResponse } from "axios";

interface Props {
    fieQuery: FIEQueryType
    updateInputValue: typeof updateInputValue
    setFIEQueriesToStore: typeof setFIEQueriesToStore
    closeModal?: Function
    isEditMode: boolean
}

interface State {
    filtersForm: FiltersFormType
    queryName: string
    queryElement: string
    isActive: intboolean | null
    viewTable: ViewTableType
    isSaving: boolean
}

type RawOrRangeType = 'rangeOne' | 'rangeTwo' | 'elements' | 'raw'

type CheckFilterValueType = {
    type: 'number' | 'select' | 'string'
    rawOrRange: RawOrRangeType
    min?: number
    max?: number
    step?: number
    transPrefix?: string
    allowedValues?: string[]
}

interface CheckFilterType {
    [key: string]: CheckFilterValueType;
}

interface ValidationTableType {
    image: CheckFilterType
    elements: CheckFilterType
    product: CheckFilterType
    image_validations: CheckFilterType
    watermark: CheckFilterType
    quality: CheckFilterType
    person: CheckFilterType
}

export const ALL_OBJECT_POSITIONS = [
    'image_in_the_middle',
    'vertical_image',
    'horizontal_image',
    'image_up',
    'image_down',
    'image_left',
    'image_right',
    'complex_background',
    'unknown_image_format',
    'ok',
];

const VALIDATION_TABLE: ValidationTableType = {
    image: {
        width: { type: 'number', min: 0, max: null, rawOrRange: 'rangeOne' },
        height: { type: 'number', min: 0, max: null, rawOrRange: 'rangeOne' },
        orientation: { type: 'select', allowedValues: ['landscape', 'portrait', 'square'], rawOrRange: 'raw' },
        is_light_bg: { type: 'select', transPrefix: 'option_', allowedValues: ['0', '1'], rawOrRange: 'raw' },
        is_solid_bg: { type: 'select', transPrefix: 'option_', allowedValues: ['0', '1'], rawOrRange: 'raw' },
        is_transparent: { type: 'select', transPrefix: 'option_', allowedValues: ['0', '1'], rawOrRange: 'raw' },
        bg_color_distance: { type: 'number', min: 0, max: 100, rawOrRange: 'raw' },
        bg_color: { type: 'string', rawOrRange: 'raw' },
        //status_code: {type: 'select', transPrefix: 'status_', allowedValues: ['200', '403', '404', '500'], rawOrRange: 'raw'},
        status_code: { type: 'number', min: 0, max: 510, rawOrRange: 'raw' },
        should_redownload: { type: 'select', transPrefix: 'option_', allowedValues: ['0', '1'], rawOrRange: 'raw' },
    },
    elements: {
    },
    product: {
        productName: { type: 'string', rawOrRange: 'raw' },
        fulltext: { type: 'string', rawOrRange: 'raw' },
        should_rerender: { type: 'select', transPrefix: 'option_', allowedValues: ['0', '1'], rawOrRange: 'raw' },
    },
    image_validations: {
        status: { type: 'select', allowedValues: ['OK', 'NOT_OK'], rawOrRange: 'raw' },
        object_position: { type: 'select', allowedValues: ALL_OBJECT_POSITIONS, rawOrRange: 'raw' },
        correctness_percentage_offset: { type: 'number', min: 0, max: 100, step: 1, rawOrRange: 'rangeTwo' },
    },
    watermark: {
        status: { type: 'select', allowedValues: ['OK', 'NOT_OK'], rawOrRange: 'raw' },
        confidence: { type: 'number', min: 0, max: 100, step: 1, rawOrRange: 'rangeTwo' },
    },
    quality: {
        status: { type: 'select', allowedValues: ['OK', 'NOT_OK'], rawOrRange: 'raw' },
        confidence: { type: 'number', min: 0, max: 100, step: 1, rawOrRange: 'rangeTwo' },
    },
    person: {
        status: { type: 'select', allowedValues: ['OK', 'NOT_OK'], rawOrRange: 'raw' },
        max_confidence: { type: 'number', min: 0, max: 100, rawOrRange: 'rangeTwo' },
    },
}

class ParseFiltersForm extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            ...this.propsToState(props.fieQuery),
            isSaving: false,
        }
    }

    propsToState = (fieQuery: FIEQueryType) => {
        return {
            filtersForm: fieQuery.filter ? JSON.parse(fieQuery.filter) : null,
            queryName: fieQuery.name ? fieQuery.name : null,
            queryElement: fieQuery.element ? fieQuery.element.replace(elementPrefix, '') : null,
            viewTable: fieQuery.viewtable ? fieQuery.viewtable : null,
            isActive: typeof fieQuery.is_active !== 'undefined' ? fieQuery.is_active : 1,
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevProps && this.props.fieQuery && JSON.stringify(prevProps.fieQuery) !== JSON.stringify(this.props.fieQuery)) {
            this.setState({
                ...this.propsToState(this.props.fieQuery),
            })
        }
    }

    renderFilter = (filterType: FType, transString: string): JSX.Element => {
        const { filtersForm } = this.state

        const parseAsRange = [
            'width',
            'height',
            'content_length',
            'object_to_image_ratio'
        ]

        const unitsByType = {
            width: 'px',
            height: 'px',
            content_length: 'kb',
            object_to_image_ratio: '%',
        }

        const items = getNonEmptyFiltersForType(filtersForm, filterType).map(key => {
            return this.printRow(key, filterType, parseAsRange.includes(key), unitsByType[key] || '')
        })

        if (items.length > 0) {
            return <div className={`parsed-${filterType}-filters`} style={{ whiteSpace: 'nowrap' }}>
                <h4>{trans(transString)}</h4>
                {items}
            </div>
        }

        return <div />
    }

    handleDeleteQuery = (fieQueryId: ID) => {
        deleteFIEQuery(fieQueryId)
            .then((response: AxiosResponse<FIEQueryType[]>) => {
                this.props.setFIEQueriesToStore(response.data)
            })
            .catch(error => {
                if (error.response && error.response.status === 409 && error.response.data.error) {
                    const message = `${trans(error.response.data.error)} (${error.response.data.template_ids.join(',')})`
                    store.dispatch(createToast(textToast('error', message)))
                } else {
                    console.error('cannot delete fie query', error)
                }
            })
            .finally(() => {
                if (this.props.closeModal) {
                    this.props.closeModal()
                }
            })
    }

    isTypeofRange = (value): boolean => {
        return (typeof value === 'object' && typeof value.from !== 'undefined' && typeof value.to !== 'undefined')
    }

    isTypeOfElementsRange = (value): boolean => {
        return (typeof value === 'object' && (typeof value.value !== 'undefined' || typeof value.secondValue !== 'undefined') && typeof value.field !== 'undefined')
    }

    changeValue = (filterType: FType, key: string, value: string | object) => {
        const { filtersForm } = this.state

        let updatedFiltersForm = { ...filtersForm }
        updatedFiltersForm[filterType][key] = value !== null && typeof value !== 'object' ? `${value}` : value

        this.setState({ filtersForm: updatedFiltersForm })
    }

    isNumeric = (str) => {
        return !isNaN(str) && !isNaN(parseFloat(str))
    }

    getError = (filterType: FType, key: string, checkThisValue: number | string | RangeType | ElementsFilterType, subkey: 0 | 1 | string | null = null): string | null => {
        if (typeof VALIDATION_TABLE[filterType] !== 'undefined' && typeof VALIDATION_TABLE[filterType][key] !== 'undefined' && typeof VALIDATION_TABLE[filterType][key] === 'object') {
            const f: CheckFilterValueType = VALIDATION_TABLE[filterType][key]
            //we have some prescriptions for the value, lets check it:

            if (typeof f.min !== 'undefined' && f.min !== null && parseFloat(`${checkThisValue}`) < f.min) {
                return `min ${f.min}`
            }
            if (typeof f.max !== 'undefined' && f.max !== null && parseFloat(`${checkThisValue}`) > f.max) {
                return `max ${f.max}`
            }
            if (f.type === 'number' && (!this.isNumeric(checkThisValue) && checkThisValue !== null && checkThisValue !== '')) {
                return trans('not_number')
            }
        }

        return null
    }

    getInputFieldType = (key: string, filterType: FType): "number" | "search" | "time" | "text" | "password" | "hidden" | "email" | "tel" | "url" | "file" => {
        const validationRow = typeof VALIDATION_TABLE[filterType] !== 'undefined' && typeof VALIDATION_TABLE[filterType][key] !== 'undefined' && typeof VALIDATION_TABLE[filterType][key] === 'object' ? VALIDATION_TABLE[filterType][key] : null
        return (validationRow && validationRow.type === 'number') ? 'number' : undefined
    }

    printRow = (key: string, filterType: FType, parseValueAsRange: boolean = false, units: string = '') => {
        const { filtersForm } = this.state
        const { isEditMode } = this.props

        const value/* : string | string[] | object | RangeType */ = filtersForm && filtersForm[filterType] && typeof filtersForm[filterType][key] !== "undefined" ? filtersForm[filterType][key] : null

        const transFrom = trans('filter_value_from').toLowerCase()
        const transTo = trans('filter_value_to').toLowerCase()

        const validationRow = typeof VALIDATION_TABLE[filterType] !== 'undefined' && typeof VALIDATION_TABLE[filterType][key] !== 'undefined' && typeof VALIDATION_TABLE[filterType][key] === 'object' ? VALIDATION_TABLE[filterType][key] : null
        const selectValues = validationRow && Array.isArray(VALIDATION_TABLE[filterType][key].allowedValues) ? VALIDATION_TABLE[filterType][key].allowedValues : []

        const values = (parseValueAsRange && typeof value === 'string' && value.includes('_')) ? value.split('_') : []
        const parsedValue = values.length === 2 ? `${values[0] ? `${transFrom} ${values[0]} ${units}` : ''} ${values[1] ? `${transTo} ${values[1]} ${units}` : ''}` : `${value} ${units}`

        const parsedTranslatedValue = (key === 'showGroup' && Array.isArray(value) && !value.includes('all')) ?
            value.map(g => isTrans(`status_${g}`) ? trans(`status_${g}`) : `${g}`).join('|') :
            key === 'status_reason' && typeof value === 'string' && isTrans(value) ? trans(value) :
                key === 'status' && typeof value === 'string' && isTrans(`status_${value}`) ? trans(`status_${value}`) :
                    selectValues.length > 0 && isTrans(`${validationRow.transPrefix || ''}${value}`) ? trans(`${validationRow.transPrefix || ''}${value}`) :
                        parsedValue

        if (value === null || value === '' || (key === 'showGroup' && Array.isArray(value) && value.includes('all')) ||
            (typeof (value) === 'object' && this.isTypeofRange(value) && !value.from && !value.to) ||
            (this.isTypeOfElementsRange(value) && !value.value)
        ) {
            return <div key={key} />
        }

        const isRangeOne = (parseValueAsRange && values)
        const isRangeTwo = (typeof value === "object" && this.isTypeofRange(value))
        const isElements = (typeof value === "object" && this.isTypeOfElementsRange(value))

        const fromValue = isRangeOne ? (values[0] ?? '') : (isRangeTwo ? value.from : (isElements ? value.value : null))
        const toValue = isRangeOne ? (values[1] ?? '') : (isRangeTwo ? value.to : (isElements ? value.secondValue : null))

        const title = isElements ? `element_${value.field}` : key

        return <KeyValueGrid dataIsEditMode={isEditMode} key={key}>
            <GridCell style={{ color: '#3a3a3a', fontWeight: 'bold', paddingLeft: '15px' }}>{isTrans(title) ? trans(title) : title}: </GridCell>

            {(isEditMode && validationRow) ?
                <GridCell className={'edit-col'}>
                    {(fromValue || toValue) ?
                        <Grid cols={'repeat(2, 1fr)'} gap={'10px'}>
                            <GridCell>
                                <TextInput
                                    value={fromValue}
                                    label={!(isElements && value.field === 'name') ? transFrom : undefined}
                                    invalid={!!this.getError(filterType, key, fromValue, 0)}
                                    error={this.getError(filterType, key, fromValue, 0)}
                                    onChange={(e) => {
                                        const newValue = isRangeOne ? `${e}_${toValue}` : isRangeTwo ? { from: e, to: toValue } : isElements ? { ...value, value: e } : null
                                        this.changeValue(filterType, key, newValue)
                                    }}
                                    onClear={() => this.changeValue(filterType, key, null)}
                                    type={this.getInputFieldType(key, filterType)}
                                    min={validationRow.min ?? undefined}
                                    max={validationRow.max ?? undefined}
                                    step={validationRow.step ?? undefined}
                                />
                            </GridCell>
                            <GridCell>
                                {!(isElements && value.field === 'name') &&
                                    <TextInput
                                        value={toValue}
                                        label={transTo}
                                        invalid={!!this.getError(filterType, key, toValue, 1)}
                                        error={this.getError(filterType, key, toValue, 1)}
                                        onChange={(e) => {
                                            const newValue = isRangeOne ? `${fromValue}_${e}` : isRangeTwo ? {
                                                from: fromValue,
                                                to: e
                                            } : isElements ? { ...value, secondValue: e } : null
                                            this.changeValue(filterType, key, newValue)
                                        }}
                                        onClear={() => this.changeValue(filterType, key, null)}
                                        type={this.getInputFieldType(key, filterType)}
                                        min={validationRow.min ?? undefined}
                                        max={validationRow.max ?? undefined}
                                        step={validationRow.step ?? undefined}
                                    />
                                }
                            </GridCell>
                        </Grid> :
                        isElements ? <TextInput
                            value={value.value}
                            invalid={!!this.getError(filterType, key, value.value, value.field)}
                            error={this.getError(filterType, key, value.value, value.field)}
                            onChange={(e) => this.changeValue(filterType, key, { ...value, value: e })}
                            onClear={() => this.changeValue(filterType, key, null)}
                            type={this.getInputFieldType(key, filterType)}
                            min={validationRow.min ?? undefined}
                            max={validationRow.max ?? undefined}
                            step={validationRow.step ?? undefined}
                        /> :
                            (selectValues.length > 0) ? <Select
                                value={value !== null ? `${value}` : value}
                                options={selectValues.map(v => {
                                    return { label: isTrans(`${validationRow.transPrefix || ''}${v}`) ? trans(`${validationRow.transPrefix || ''}${v}`) : `${v}`, value: v }
                                })}
                                onClear={() => this.changeValue(filterType, key, null)}
                                onChange={(e) => this.changeValue(filterType, key, e)}
                            />
                                :
                                <TextInput
                                    value={value}
                                    onChange={(e) => this.changeValue(filterType, key, e)}
                                    onClear={() => this.changeValue(filterType, key, null)}
                                    invalid={!!this.getError(filterType, key, value)}
                                    error={this.getError(filterType, key, value)}
                                    type={this.getInputFieldType(key, filterType)}
                                    min={validationRow.min ?? undefined}
                                    max={validationRow.max ?? undefined}
                                    step={validationRow.step ?? undefined}
                                />
                    }
                </GridCell> :
                <GridCell>
                    {isRangeTwo ? `${transFrom} ${value.from ?? '?'} ${units} ${transTo} ${value.to ?? '?'} ${units}` : isElements ?
                        ((typeof value.secondValue !== 'undefined' && value.secondValue !== null && value.secondValue !== '') ? `${transFrom} ${value.value ?? ''} ${transTo} ${value.secondValue ?? ''}` : `${value.value ?? ''}`) :
                        parsedTranslatedValue}
                </GridCell>
            }
        </KeyValueGrid>
    }

    render() {
        const { queryName, queryElement, viewTable, filtersForm, isSaving, isActive } = this.state
        const { isEditMode } = this.props

        return (
            <StyledContentDiv>

                {isEditMode &&
                    <Grid cols={'auto auto'} style={{ alignSelf: 'center', marginBottom: '15px' }} gap={'10px'}>
                        <GridCell>{trans('fie_query_is_active')}:</GridCell>
                        <GridCell>
                            <Toggler
                                value={isActive}
                                onChange={(e) => this.setState({ isActive: e ? 1 : 0 })}
                            />
                        </GridCell>

                        <GridCell>{trans('fie_query_name')}:</GridCell>
                        <GridCell>
                            <TextInput
                                value={queryName}
                                onChange={(e) => {
                                    this.setState({ queryName: e })
                                }}
                            />
                        </GridCell>

                        <GridCell>{trans('fie_query_element')}: {elementPrefix}</GridCell>
                        <GridCell>
                            <TextInput
                                value={queryElement}
                                onChange={(e) => this.setState({ queryElement: e.replace(elementPrefix, '') })}
                            />
                        </GridCell>
                    </Grid>
                }

                {this.renderFilter('product', 'products')}
                {this.renderFilter('elements', 'product_elements')}
                {this.renderFilter('image', 'images')}
                {this.renderFilter('image_validations', 'validations')}
                {this.renderFilter('watermark', 'watermarks')}
                {this.renderFilter('quality', 'images_quality')}
                {this.renderFilter('person', 'has_person')}

                {isEditMode &&
                    <>
                        <GridCell col={'1/-1'} />

                        <GridCell></GridCell>
                        <GridCell col={'span 2'} style={{ textAlign: 'right' }}>
                            {isSaving ?
                                <Spinner type={'bubbles'} />
                                :
                                <div style={{ marginTop: '20px' }}>
                                    <StyledButton
                                        color={Theme.green}
                                        onClick={() => {
                                            this.setState({ isSaving: true })
                                            saveFilterAsQuery(filtersForm, queryName, queryElement, viewTable, isActive, this.props.fieQuery.id)
                                                .then((response: AxiosResponse<FIEQueryType[]>) => {
                                                    this.props.setFIEQueriesToStore(response.data)
                                                })
                                                .catch(error => {
                                                    console.error('cannot save', error.response)
                                                })
                                                .finally(() => {
                                                    this.setState({ isSaving: false })
                                                    if (this.props.closeModal) {
                                                        this.props.closeModal()
                                                    }
                                                })

                                        }}>
                                        <Icon type={'save-light'} text={trans('save')} color={'#fff'} />
                                    </StyledButton>

                                    <StyledButton
                                        color={Theme.red}
                                        onClick={() => this.handleDeleteQuery(this.props.fieQuery.id)}>
                                        <Icon type={'trash'} text={trans('delete')} color={'#fff'} />
                                    </StyledButton>

                                    {this.props.fieQuery.mergado_query_id &&
                                        <StyledButton
                                            color={'#000'}
                                            onClick={() => {
                                                Mergado.openWindow(`https://app.mergado.com/projects/${projectId}/queries/edit/${this.props.fieQuery.mergado_query_id}`)
                                            }}>
                                            <Icon type={'mergado'} text={trans('show_in_mergado')} />
                                        </StyledButton>
                                    }
                                </div>
                            }
                        </GridCell>
                    </>
                }
            </StyledContentDiv>
        )
    }
}

const StyledButton = css("div")(props => {
    return ({
        display: 'inline-block',
        color: '#fff',
        padding: '5px 10px',
        margin: '0 4px',
        cursor: 'pointer',
        borderRadius: Theme.radius,
        fontWeight: 'bold',
        background: props.color,
    })
})

const StyledContentDiv = css('div')({
    ' h4': {
        textTransform: 'uppercase',
        color: iconDisabledColor,
    }
})

const KeyValueGrid = css(Grid)({
    gridTemplateColumns: 'auto auto',
    gridGap: '20px',
    ' .muk-gridcell': {
        alignSelf: 'center',
    },
    ' .edit-col': {
    },
    ' .m-field-muk-select': {
        marginBottom: 0,
    }
}, props => {
    return {
        /*marginBottom:  props.dataIsEditMode ? '10px' : 0,*/
    }
})

function mapStateToProps(state) {
    return {
        activeFIEQueryId: state.activeFIEQueryId,
    }
}

export default connect(mapStateToProps, {
    updateInputValue,
    setFIEQueriesToStore,
})(ParseFiltersForm)